import { createFileRoute } from "@tanstack/react-router";
import { Col, Container, Row, Table } from "react-bootstrap";
import { useFetch } from "../../../../../hooks/api";
import { GrStatusGoodSmall, GrStatusDisabledSmall } from "react-icons/gr";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Accordion } from "../../../../../components/core/basic/Accordion";
import { keepPreviousData } from "@tanstack/react-query";

interface FawcettItem {
  id: number;
  channel_id: number;
  enabled: boolean;
  links: {
    inventory: string;
    capabilities: string;
  };
}

interface ReportItem {
  report_id: number;
  status: string;
  created: string;
  report_type: string;
}

interface ReportCardResponse {
  org: { id: number; name: string; org_type: string; profile_type: string; status: string; days_active: number };
  anomalies: { name: string; status: string; anomalies?: { message: string } }[];
  reports: {
    pending: ReportItem[];
    iffy: ReportItem[];
  };
  fawcett: Record<string, FawcettItem[]>;
}

interface AnomaliesProps {
  anomalies: ReportCardResponse["anomalies"];
}

const Anomalies = ({ anomalies }: AnomaliesProps) => {
  const { t } = useTranslation(["admin"]);
  return (
    <>
      {anomalies.length === 0 ?
        <div className={"alert alert-info"}>
          {t(($) => $.admin.reportCard["No anomalies found within the last 14 days."])}
        </div>
      : <Table striped bordered hover>
          <thead>
            <tr>
              <th>{t(($) => $.admin.reportCard["Name"])}</th>
              <th>{t(($) => $.admin.reportCard["Status"])}</th>
              <th>{t(($) => $.admin.reportCard["Message"])}</th>
            </tr>
          </thead>
          <tbody>
            {anomalies.map((item, index) => {
              return (
                <tr key={index}>
                  <td>{item.name}</td>
                  <td>{item.status}</td>
                  <td>{item.anomalies?.message}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      }
    </>
  );
};

interface FawcettProps {
  fawcett: ReportCardResponse["fawcett"];
}

const Fawcett = ({ fawcett }: FawcettProps) => {
  const { t } = useTranslation(["admin"]);
  return (
    <>
      {Object.keys(fawcett).length === 0 ?
        <div className={"alert alert-info"}>{t(($) => $.admin.reportCard["No Fawcett data found"])}</div>
      : <Table striped bordered hover>
          <thead>
            <tr>
              <th></th>
              <th>{t(($) => $.admin.reportCard["Channel"])}</th>
              <th>{t(($) => $.admin.reportCard["ID"])}</th>
              <th>{t(($) => $.admin.reportCard["Channel ID"])}</th>
              <th>{t(($) => $.admin.reportCard["Links"])}</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(fawcett).map((key) => {
              return fawcett[key]!.map((item, index) => {
                return (
                  <Fragment key={index}>
                    <tr>
                      <td className={"text-center"}>
                        {item.enabled ?
                          <GrStatusGoodSmall color="green" />
                        : <GrStatusDisabledSmall color="red" />}
                      </td>
                      <td className={"text-center"}>{key}</td>
                      <td className={"text-center"}>{item.id}</td>
                      <td className={"text-center"}>{item.channel_id}</td>
                      <td className={"text-end"}>
                        <ul className={"list-group list-group-horizontal-sm d-flex justify-content-end"}>
                          <a
                            href={`https://fawcett.growthvectors.io/admin/backend/credential/${item.id}`}
                            className={"list-group-item"}
                          >
                            {t(($) => $.admin.reportCard["Admin"])}
                          </a>
                          <a href={item.links.inventory} className={"list-group-item"}>
                            {t(($) => $.admin.reportCard["Inventory"])}
                          </a>
                          <a href={item.links.capabilities} className={"list-group-item"}>
                            {t(($) => $.admin.reportCard["Capabilities"])}
                          </a>
                        </ul>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={5}>
                        <Accordion
                          title={t(($) => $.admin.reportCard["Details"])}
                          defaultExpanded={false}
                          marginTop={0}
                        >
                          <pre>{JSON.stringify(item, null, 2)}</pre>
                        </Accordion>
                      </td>
                    </tr>
                  </Fragment>
                );
              });
            })}
          </tbody>
        </Table>
      }
    </>
  );
};

interface ImportReportTableProps {
  reports: ReportItem[];
}

const ImportReportTable = ({ reports }: ImportReportTableProps) => {
  const { t } = useTranslation(["admin"]);
  return (
    <Table striped bordered hover className={"mt-4"}>
      <thead>
        <tr>
          <th>{t(($) => $.admin.reportCard["Report ID"])}</th>
          <th>{t(($) => $.admin.reportCard["Status"])}</th>
          <th>{t(($) => $.admin.reportCard["Created"])}</th>
          <th>{t(($) => $.admin.reportCard["Type"])}</th>
          <th>{t(($) => $.admin.reportCard["Links"])}</th>
        </tr>
      </thead>
      <tbody>
        {reports.map((item, index) => {
          return (
            <tr key={index}>
              <td>{item.report_id}</td>
              <td>{item.status}</td>
              <td>{item.created}</td>
              <td>{item.report_type}</td>
              <td className={"text-end"}>
                <ul className={"list-group list-group-horizontal-sm d-flex justify-content-end"}>
                  <a
                    href={`https://fawcett.growthvectors.io/api/reporting/warehouse/${item.report_id}`}
                    className={"list-group-item"}
                  >
                    {t(($) => $.admin.reportCard["Warehouse"])}
                  </a>
                </ul>
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};

const ReportCard = () => {
  const { t } = useTranslation(["admin"]);
  const { org_id } = Route.useParams();
  const { data, isFetching } = useFetch<ReportCardResponse>(
    ["reportcard", org_id],
    `/api/reportcard/${org_id}/`,
    {},
    {
      select: (response) => response.data,
      retry: 3,
      placeholderData: keepPreviousData,
    },
  );

  return (
    <Container
      style={{
        minHeight: "100%",
      }}
    >
      <Row>
        <Col>
          <h1 className={"fs-1"}>{t(($) => $.admin.reportCard["Organization Report Card"])}</h1>
          <p>{t(($) => $.admin.reportCard["A summary of the currently active organization's internal state."])}</p>
          <ul className={"list-group list-group-horizontal-sm"}>
            <a href={"/user/staff/reportcard/"} className={"list-group-item"}>
              {t(($) => $.admin.reportCard["View System Report Card"])}
            </a>
            {isFetching && (
              <li className={"list-group-item"}>
                <i className="fa fa-spinner fa-spin"></i> {t(($) => $.admin.reportCard["Updating..."])}
              </li>
            )}
          </ul>
          <hr />
        </Col>
      </Row>
      {data && (
        <>
          <Row>
            <Col>
              <Table striped bordered hover>
                <tbody>
                  <tr>
                    <td>{t(($) => $.admin.reportCard["ID"])}</td>
                    <td>{data.org.id}</td>
                  </tr>
                  <tr>
                    <td>{t(($) => $.admin.reportCard["Name"])}</td>
                    <td>{data.org.name}</td>
                  </tr>
                  <tr>
                    <td>{t(($) => $.admin.reportCard["Type"])}</td>
                    <td>
                      {data.org.org_type}
                      &nbsp;({data.org.profile_type})
                    </td>
                  </tr>
                  <tr>
                    <td>{t(($) => $.admin.reportCard["Status"])}</td>
                    <td>
                      {data.org.status}
                      &nbsp;(
                      {t(($) => $.admin.reportCard["{count} days"], { count: data.org.days_active })})
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Col>
          </Row>
          <Row>
            <Col>
              <Accordion title={t(($) => $.admin.reportCard["Anomalies"])} defaultExpanded={true}>
                <Anomalies anomalies={data.anomalies} />
              </Accordion>
            </Col>
          </Row>
          <Row>
            <Col>
              <Accordion title={t(($) => $.admin.reportCard["Fawcett"])} defaultExpanded={true}>
                <Fawcett fawcett={data.fawcett} />
              </Accordion>
            </Col>
          </Row>
          <Row>
            <Col>
              <Accordion title={t(($) => $.admin.reportCard["Reports Awaiting Import"])} defaultExpanded={true}>
                {data.reports.pending.length === 0 ?
                  <div className={"alert alert-info"}>
                    {t(($) => $.admin.reportCard["No pending reports found within the last 14 days."])}
                  </div>
                : <ImportReportTable reports={data.reports.pending} />}
              </Accordion>
            </Col>
          </Row>
          <Row>
            <Col>
              <Accordion
                title={t(($) => $.admin.reportCard["Recently Failed Report Processing"])}
                defaultExpanded={true}
              >
                {data.reports.iffy.length === 0 ?
                  <div className={"alert alert-info"}>
                    {t(($) => $.admin.reportCard["No failed reports found within the last 14 days."])}
                  </div>
                : <ImportReportTable reports={data.reports.iffy} />}
              </Accordion>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export const Route = createFileRoute("/_authenticated/user/staff/reportcard/$org_id")({
  component: ReportCard,
});
